import { getHorizontalBarBackgroundStyle } from "@omnia/shared/nutrition/colors"
import { IOmniaShell, OmniaDialogSize } from "../../omnia"
import { INutrientGroup, INutritionService, IPlanEditArguments } from "../../types"
import { calculateRequirements, editPlan } from "../../utils"
import { CroppingGroup } from "./dashboard"
import { PlanCroppingVm } from "./vm/planCroppingVm"
import { PlanVm } from "./vm/planVm"
import { selectMany } from "@omnia/shared/utils/array"
import { hasAuthorizationsOnAnyInGroup } from "./permissions"

export class TableDashboardContext {

    public cropping: PlanCroppingVm[];
    private _croppingGroups: CroppingGroup[];
    private _grouped: boolean;

    public selectedPlan: PlanVm;

    public showRequirementsLeft: boolean;

    public showZones: boolean;
    public nutrientGroup: INutrientGroup;

    private _edit: boolean;

    constructor(private svc: INutritionService, private shell: IOmniaShell, public refresh: () => void) {
    }

    public setupCropping(cropping: PlanCroppingVm[]) {

        this.cropping = cropping;

    }

    get readonly() {
        return this.svc.readOnly
    }

    get selectionStr(): string {
        return this.cropping.filter(c => c.selected).map(c => c.croppingId + "-" + (c.isZone ? c.zoneNumber : "")).join(';');
    }

    get edit(): boolean {
        return this._edit;
    }

    set edit(value: boolean) {
        this._edit = value;
        this.shell.disableMenu = value;
        if (!value && this.cropping) {
            this.cropping.filter(c => c.selected).forEach(c => c.selected = false);
        }
    }

    get croppingGroups(): CroppingGroup[] {
        return this._croppingGroups;
    }

    set croppingGroups(value: CroppingGroup[]) {

        this._croppingGroups = value;
        this._grouped = value && ((value.length > 1) || ((value.length == 1) && !!value[0].headerText));

    }

    get grouped(): boolean {
        return this._grouped;
    }


    canChangeSelection(): boolean {

        return true;

    }

    toggleSelection(c: PlanCroppingVm) {

        if (this.edit && !c.selected && c.hasRequirements) {
            this.cropping.filter(c => c.selected).forEach(c => c.selected = false);
            c.selected = !c.selected;
        } else if (!this.edit && !c.isZone) {
            c.selected = !c.selected;
        }

    }

    getValueCellStyle(c: PlanCroppingVm, key: string) {

        if (c && c.requirements) {

            var value = c.requirements["Required" + key];

            return getHorizontalBarBackgroundStyle(key, value);

        }

    }


    calculateRequirements(cropping: PlanCroppingVm[]): ng.IPromise<boolean> {

        if (!cropping) {
            cropping = this.cropping.filter(c => c.selected);
        }

        if (!cropping.length) {
            cropping = this.cropping;
        }

        const croppingIds = cropping.map(c => c.croppingId)

        return this.refreshIfOk(calculateRequirements(this.svc, this.shell, croppingIds))

    }


    editRequirements() {

        this.edit = true;

    }


    getTargetCroppingForGroup(group: CroppingGroup, updateSelectionStatus: boolean): PlanCroppingVm[] {

        var cropping: PlanCroppingVm[];

        if (group) {

            var cropping = group.cropping.filter(c => c.selected);
            if (!cropping.length) {

                cropping = group.cropping;

                if (updateSelectionStatus) {
                    this.cropping.forEach(c => c.selected = false);
                    group.cropping.forEach(c => c.selected = true);
                }

            }

        }

        return cropping;

    }

    calculateGroupRequirements(group: CroppingGroup) {

        this.calculateRequirements(this.getTargetCroppingForGroup(group, true));

    }

    canCreateNewPlan(cropping: PlanCroppingVm[]): boolean {

        if (!cropping) {
            return true
        }

        return cropping.every(c => !!c.requirements) && hasAuthorizationsOnAnyInGroup(cropping)

    }

    createNewPlan(cropping: PlanCroppingVm[]): ng.IPromise<boolean> {

        if (!cropping) {
            cropping = selectMany(this.croppingGroups.map(g => g.getSelectedCropping()));
        }

        cropping = cropping.filter(c => c.selected);

        if (!this.canCreateNewPlan(cropping)) return this.shell.resolve(false);

        let args: IPlanEditArguments = {
            plan: null,
            initialCropping: cropping.map(c => c.croppingVm)
        };

        return this.editPlan(args)

    }

    canCreateNewPlanForGroup(group: CroppingGroup): boolean {

        return this.canCreateNewPlan(this.getTargetCroppingForGroup(group, false));

    }

    createNewPlanForGroup(group: CroppingGroup) {

        this.createNewPlan(this.getTargetCroppingForGroup(group, true));

    }

    updatePlan(planVm: PlanVm, $event: ng.IAngularEvent) {

        this.selectedPlan = planVm;

        let args: IPlanEditArguments = {
            plan: planVm.plan,
            initialCropping: null
        };

        if ($event) {
            $event.stopPropagation();
        }

        return this.editPlan(args)

    }


    private editPlan(args: IPlanEditArguments) {
        return this.refreshIfOk(editPlan(this.svc, this.shell, args))
    }

    resetRequirements(croppingIds: number[]): ng.IPromise<boolean> {
        return this.showDialog("omnia-nutri-requirements-reset", croppingIds, "md");
    }

    private showDialog(componentId: string, args: any, size: OmniaDialogSize): ng.IPromise<any> {

        const p = this.shell.showComponentAsDialog(componentId, args, {
            size,
            backdrop: "static"
        });

        return this.refreshIfOk(p)

    }

    private refreshIfOk(promise: ng.IPromise<boolean>) {

        return promise.then(ok => {

            if (ok) {
                this.refresh()
            }

            return ok

        })
    }

}
